import React from 'react'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { connect as connectRedux } from 'react-redux'
import { compose } from 'recompose'
import pick from 'lodash/pick'
import ReactPlayerLoader from '@brightcove/react-player-loader'
import connectHistory from 'gui/components/router/connect'
import { List } from 'immutable'

/**
 * On video player success
 * @param {Object} success The success object
 * @param {Object} success.ref The player ref
 * @param {Object} options The options
 * @param {String} [options.src] The video stream src if video id is not provided
 * @param {String} [options.srcTextTrackThumnail] The thumbnail text track source
 * @param {String} [options.type] The video src mime type
 * @param {String|Number} [options.videoId] The brigthcove video id is src is not provided
 * @param {Number} [options.accountId] The brightcove account id defaults to config value
 * @param {String} [options.playerId] The brightcove player id defaults to config value
 * @param {Map} options.config The config state
 * @param {List} [options.subtitles=List()] A list of subtitles
 * @param {List} [options.captions=List()]  A list of closed captions
 */
const onSuccess = async (success, options = {}) => {
  const {
    src, srcTextTrackThumnail, type, subtitles, captions,
  } = options
  const { ref } = success
  ref.ready(function onReady () {
    const player = this
    if (src) {
      player.src({ src, type })
    }
    subtitles.forEach((v) => {
      player.addRemoteTextTrack({
        src: v.get('url'),
        kind: 'subtitles',
        label: v.get('language'),
        language: v.get('language'),
      }, false)
    })
    captions.forEach((v) => {
      player.addRemoteTextTrack({
        src: v.get('url'),
        kind: 'captions',
        label: v.get('language'),
        language: v.get('language'),
      }, false)
    })
    if (srcTextTrackThumnail) {
      player.addRemoteTextTrack({
        src: srcTextTrackThumnail,
        kind: 'metadata',
        label: 'thumbnails',
        mode: 'hidden',
      }, false)
    }
    return player
  })
}

/**
 * Video player component to display video playback
 * @param {Object} props The props
 * @param {String} [props.src] The video stream src if video id is not provided, src is taken first
 * @param {String} [props.srcTextTrackThumnail] The thumbnail text track source
 * @param {String} [props.type] The video src mime type
 * @param {String|Number} [props.videoId]
 *   The brigthcove video id if src is not provided, src is taken first
 * @param {Number} [props.accountId] The brightcove account id defaults to config value
 * @param {String} [props.playerId] The brightcove player id defaults to config value
 * @param {Map} props.config The config state
 * @param {List} [props.subtitles=List()] A list of subtitles
 * @param {List} [props.captions=List()]  A list of closed captions
 */
function VideoPlayer (props = {}) {
  const {
    config,
    src,
    type,
    videoId,
    subtitles,
    captions,
    srcTextTrackThumnail,
  } = props
  const {
    accountId = config.getIn(['data', 'brightcove', 'accountId']),
    playerId = config.getIn(['data', 'brightcove', 'playerId']),
  } = props
  // There is no video to load
  if (!src && !videoId) {
    return null
  }
  return (
    <ReactPlayerLoader
      accountId={accountId}
      playerId={playerId}
      // Prefer src over video id
   //   videoId={src || videoId}
      onSuccess={(e) => onSuccess(e, {
        src, type, subtitles, captions, srcTextTrackThumnail,
      })}
    />
  )
}

VideoPlayer.propTypes = {
  src: PropTypes.string,
  srcTextTrackThumnail: PropTypes.string,
  type: PropTypes.string.isRequired,
  videoId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  accountId: PropTypes.number,
  playerId: PropTypes.string,
  config: ImmutablePropTypes.map.isRequired,
  subtitles: ImmutablePropTypes.list,
  captions: ImmutablePropTypes.list,
}

VideoPlayer.defaultProps = {
  captions: List(),
  subtitles: List(),
  type: 'application/x-mpegURL',
}

/**
 * Returns video player component
 * @type {typeof VideoPlayer} A video player component
 */
export default compose(
  connectRedux(
    (state) => pick(state, ['config']),
  ),
  connectHistory(),
)(VideoPlayer)
